import React from 'react'
import { useTranslation } from 'react-i18next'
import { getSelectCountryFilter } from 'components/react-select/CountryFunctions'
import {
  FieldPath, FieldPathByValue, FieldValues,
} from 'react-hook-form'
import TraderSearch from 'features/declaration/form/components/TraderSearch'
import useCodelist from 'features/declaration/codelist/api/useCodelist'
import EoriSearch from 'features/declaration/form/components/EoriSearch'
import {
  ADDRESS_LINE_MAX_LENGTH,
  ADDRESS_POST_CODE_MAX_LENGTH,
  ADDRESS_STREET_MAX_LENGTH,
  ADDRESS_STREET_NUMBER_MAX_LENGTH,
  TRANSIT_PARTY_NAME_MAX_LENGTH,
} from 'shared/ncts/common/constants'
import TypeOfPerson from 'features/declaration/form/components/TypeOfPerson'
import { ConsignorFormValues as ConsignorType, TraderType } from 'features/declaration/models/schemas/tradersSchema'
import Input from '../../../../../components/p5/Input'
import FormSelect from '../../../../../components/p5/FormSelect'
import FormField from '../../../../../components/p5/FormField'
import FormCollapsibleColumn from '../../../../../components/p5/FormCollapsibleColumn'
import DotLeafPaths from '../../../../../helpers/dot-leaf-paths'
import { NctsScope } from '../../../../../shared/ncts/types'
import useDeclarationFieldRequirements
  from '../useDeclarationFieldRequirements'

type ConsignorRelPath = DotLeafPaths<ConsignorType>

type TraderBasePath<TForm extends FieldValues> = FieldPathByValue<TForm, TraderType | null | undefined>

interface ConsignorProps<TForm extends FieldValues> {
  scope: TraderBasePath<TForm>
  initialIsSectionOpen?: boolean
  nctsScope: NctsScope
}

function Consignor<TForm extends FieldValues>({
  scope,
  initialIsSectionOpen,
  nctsScope,
}: Readonly<ConsignorProps<TForm>>) {
  const { t } = useTranslation()
  const [, countriesOptions] = useCodelist('COUNTRIES')

  const field = (rel: ConsignorRelPath) => `${scope}.${rel}` as FieldPath<TForm>

  const fieldRequirements = useDeclarationFieldRequirements<TForm>(nctsScope, [
    'consignor',
    'consignor.identificationNumber',
    'consignor.name',
    'consignor.typeOfPerson',
    'consignor.address',
    'consignor.address.country',
    'consignor.address.addressLine',
    'consignor.address.street',
    'consignor.address.streetNumber',
    'consignor.address.city',
    'consignor.address.postcode',
    'consignor.contactPerson',
    'consignor.contactPerson.name',
    'consignor.contactPerson.telephone',
    'consignor.contactPerson.email',
  ])

  const isAddressLineForbidden = fieldRequirements['consignor.address.addressLine']?.requirement === 'forbidden'
  const isAddressStreetForbidden = fieldRequirements['consignor.address.street']?.requirement === 'forbidden'
  const isShowAddressLine = !isAddressLineForbidden || isAddressStreetForbidden

  return (
    <section className="py-3">
      <FormCollapsibleColumn
        columnId="declaration-consignor"
        headingLabel={t('declaration.p5.consignor')}
        initialIsSectionOpen={initialIsSectionOpen}
        fieldRule={fieldRequirements.consignor}
      >
        <FormField
          labelKey="declaration.p5.consignorIdentificationNr"
          fieldRule={fieldRequirements['consignor.identificationNumber']}
        >
          <EoriSearch<TForm, typeof scope>
            scope={scope}
            traderTarget="CONSIGNOR"
            fieldRule={fieldRequirements['consignor.identificationNumber']}
          />
        </FormField>
        <FormField
          scope={`${scope}.name`}
          labelKey="declaration.p5.consignorName"
          fieldRule={fieldRequirements['consignor.name']}
        >
          <TraderSearch
            scope={scope}
            maxLength={TRANSIT_PARTY_NAME_MAX_LENGTH}
            traderTarget="CONSIGNOR"
            fieldRule={fieldRequirements['consignor.name']}
          />
        </FormField>
        <TypeOfPerson
          scope={scope}
          fieldRule={fieldRequirements['consignor.typeOfPerson']}
        />
        <FormField
          labelKey="declaration.p5.country"
          fieldRule={fieldRequirements['consignor.address.country']}
        >
          <FormSelect
            field={field('address.country')}
            labelKey="declaration.p5.country"
            options={countriesOptions}
            customFilterOption={getSelectCountryFilter}
            type="sync"
            fieldRule={fieldRequirements['consignor.address.country']}
          />
        </FormField>
        <div className="row party-address-row">
          <FormField
            className="col-12"
            labelKey="declaration.p5.addressLine"
            fieldRule={fieldRequirements['consignor.address.addressLine']}
            hidden={!isShowAddressLine}
          >
            <Input
              field={field('address.addressLine')}
              autoComplete={`${scope}.address.addressLine`}
              labelKey="declaration.p5.addressLine"
              type="text"
              maxLength={ADDRESS_LINE_MAX_LENGTH}
              placeholder={t('declaration.p5.addressLine')}
              fieldRule={fieldRequirements['consignor.address.addressLine']}
            />
          </FormField>
          <FormField
            className="col-6"
            labelKey="declaration.p5.street"
            fieldRule={fieldRequirements['consignor.address.street']}
            hidden={isShowAddressLine}
          >
            <Input
              field={field('address.street')}
              autoComplete={`${scope}.address.street`}
              labelKey="declaration.p5.street"
              type="text"
              maxLength={ADDRESS_STREET_MAX_LENGTH}
              placeholder={t('declaration.p5.street')}
              fieldRule={fieldRequirements['consignor.address.street']}
            />
          </FormField>
          <FormField
            className="col-6"
            labelKey="declaration.p5.streetNumber"
            fieldRule={fieldRequirements['consignor.address.streetNumber']}
            hidden={isShowAddressLine}
          >
            <Input
              field={field('address.streetNumber')}
              autoComplete={`${scope}.address.streetNumber`}
              labelKey="declaration.p5.streetNumber"
              type="text"
              maxLength={ADDRESS_STREET_NUMBER_MAX_LENGTH}
              placeholder={t('declaration.p5.streetNumber')}
              fieldRule={fieldRequirements['consignor.address.streetNumber']}
            />
          </FormField>
        </div>
        <div className="row party-address-row">
          <FormField
            className="col-8"
            labelKey="declaration.p5.city"
            fieldRule={fieldRequirements['consignor.address.city']}
          >
            <Input
              field={field('address.city')}
              autoComplete={`${scope}.address.city`}
              labelKey="declaration.p5.city"
              maxLength={35}
              type="text"
              placeholder={t('declaration.p5.city')}
              fieldRule={fieldRequirements['consignor.address.city']}
            />
          </FormField>
          <FormField
            className="col-4"
            labelKey="declaration.p5.postcode"
            fieldRule={fieldRequirements['consignor.address.postcode']}
          >
            <Input
              field={field('address.postcode')}
              autoComplete={`${scope}.address.postcode`}
              labelKey="declaration.p5.postcode"
              maxLength={ADDRESS_POST_CODE_MAX_LENGTH}
              type="text"
              placeholder={t('declaration.p5.postcode')}
              fieldRule={fieldRequirements['consignor.address.postcode']}
            />
          </FormField>
        </div>
        <FormField
          labelKey="declaration.p5.contactPersonName"
          fieldRule={fieldRequirements['consignor.contactPerson.name']}
        >
          <Input
            field={field('contactPerson.name')}
            autoComplete={`${scope}.contactPerson.name`}
            labelKey="declaration.p5.contactPersonName"
            maxLength={70}
            type="text"
            placeholder={t('declaration.p5.contactPersonName')}
            fieldRule={fieldRequirements['consignor.contactPerson.name']}
          />
        </FormField>
        <FormField
          labelKey="declaration.p5.contactPersonPhone"
          fieldRule={fieldRequirements['consignor.contactPerson.telephone']}
        >
          <Input
            field={field('contactPerson.telephone')}
            autoComplete={`${scope}.contactPerson.telephone`}
            labelKey="declaration.p5.contactPersonPhone"
            maxLength={35}
            type="text"
            placeholder={t('declaration.p5.contactPersonPhone')}
            fieldRule={fieldRequirements['consignor.contactPerson.telephone']}
          />
        </FormField>
        <FormField
          labelKey="declaration.p5.contactPersonEmail"
          fieldRule={fieldRequirements['consignor.contactPerson.email']}
        >
          <Input
            field={field('contactPerson.email')}
            autoComplete={`${scope}.contactPerson.email`}
            labelKey="declaration.p5.contactPersonEmail"
            maxLength={256}
            type="text"
            placeholder={t('declaration.p5.contactPersonEmail')}
            fieldRule={fieldRequirements['consignor.contactPerson.email']}
          />
        </FormField>
      </FormCollapsibleColumn>
    </section>
  )
}

Consignor.defaultProps = {
  initialIsSectionOpen: true,
}

export default Consignor
